import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { login, authorizeToken } from '../_slice/onboardingSlice';
import { setorganizationData } from '../_slice/organizationSlice';

import CustomInput from '../widgets/CustomInput';
import CustomBtn from '../widgets/CustomBtn';
import AppContext from '../context/AppContext';

import logoImg from '../../assets/images/lociLogo.png';
import closeEye from '../../assets/images/close_eye.png';
import openEye from '../../assets/images/open_eye.png';
import rightTick from "../../assets/images/tickRight.png";
import crosstick from "../../assets/images/crosssIcon.png"
/**
 * SignIn component for user login.
 * @component
 */
function SignIn() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const onboardingData = useSelector(state => state?.onboarding);

  // Effect to dispatch setorganizationData only once when user data is available
  useEffect(() => {
    if (onboardingData?.data?.user) {
      dispatch(setorganizationData(onboardingData));
    }
  }, [onboardingData?.data?.user, dispatch]);

  // State to manage password field focus and visibility
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState({
    password: false,
  });

  // Setting up form handling with react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      remember_me: false
    }
  });

  const watchPassword = watch('password', '');
  /**
   * Handle form submission.
   * @param {Object} data - Form data containing email, password, and remember_me.
   * @returns {Promise<void>}
   */
  const onSubmit = (data) => {
    if (!isPasswordValid(watchPassword)) {
      setIsPasswordFocused(true);
      return;
    }
    dispatch(
      login({
        initialPost: data,
        navigate,
        navigatePath: '/organization',
        setLoader
      })
    );
  };

  // Function to validate password strength
  const isPasswordValid = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`])[A-Za-z\d!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]{12,}$/.test(password);

  // Function to determine color based on password validity
  const getColor = (password) => (password === '' ? '' : isPasswordValid(password) ? '#292929' : 'red');

  // Function to generate validation message for password rules
  const getValidationMessage = (regex, message, password) => {
    const isValid = regex.test(password);
    const icon = regex.test(password) ? <img src={rightTick} alt='' /> : <img src={crosstick} alt='' />;
    const space = '\u00A0\u00A0';
    const colorStyle = isValid ? { color: '#05C680' } : { color: getColor(password) };
    return (
      <div style={{ ...colorStyle, marginTop: "15px" }} >
        {icon} {space} {message}
      </div>
    );
  };

  // Check if all password conditions are met
  const allConditionsMet = isPasswordValid(watch('password'));

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='onboarding-box'>
          <div className='onboarding-box-header'>
            <img src={logoImg} alt='Logo' className='onboarding-box-header-img' />
            <p className='onboarding-box-header-text'>The Evolution of Safety.</p>
          </div>

          {/* Email input field */}
          <div className='mb-4'>
            <CustomInput
              label='Email'
              type='email'
              classname='custom_input'
              tabIndex='1'
              {...register('email', {
                required: 'Email required',
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/i,
                  message: 'Invalid email address'
                }
              })}
            />
            {errors.email && <p className='text-danger'>{errors.email.message}</p>}
          </div>

          {/* Password input field */}
          <div className='mb-3 position-relative'>
            <CustomInput
              label='Password'
              type={showPassword.password ? 'text' : 'password'}
              icon={true}
              src={showPassword.password ? openEye : closeEye}
              onClick={() =>
                setShowPassword((prev) => ({
                  ...prev,
                  password: !prev.password,
                }))
              }
              classname='custom_input padding_right'
              tabIndex='1'
              {...register('password', {
                required: 'Password required',
                // pattern: {
                //   value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@!_%&*]).{14,}$/,
                //   message: 'Password must have at least 14 characters, having uppercase, lowercase letters, digits and special characters'
                // }
              })}
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => setIsPasswordFocused(false)}
            />
            {errors.password && <p className='text-danger'>{errors.password.message}</p>}
            {/* Display password strength rules */}
            {isPasswordFocused && !allConditionsMet && (
              <div style={{ color: getColor(watchPassword) }} className='password_strengthbox'>
                <b className='password_heading mb-4'>Password must be</b>
                {getValidationMessage(/.{12,}$/, ' Minimum 12 characters', watchPassword)}
                {getValidationMessage(/(?=.*[a-z])/, ' At least one lowercase letter', watchPassword)}
                {getValidationMessage(/(?=.*[A-Z])/, ' At least one uppercase letter', watchPassword)}
                {getValidationMessage(/(?=.*\d)/, ' At least one number', watchPassword)}
                {getValidationMessage(/[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]/, 'At least one special character', watchPassword)}
              </div>
            )}
            <Link to='forgot-password' className='forgotPassword'>Forgot Password?</Link>
          </div>

          {/* Remember me checkbox */}
          <div className='remberme mt-5 mb-5'>
            <label className='check_box'>
              Remember me
              <input type='checkbox' {...register('remember_me')} />
              <span className='checkbox_check'></span>
            </label>
          </div>

          <div className='onboarding-box-footer justify-content-between'>
            <Link to='signup' className='onboarding-box-footer-text mr-3'>Create Account</Link>
            <CustomBtn buttonText='Login' type='submit' id='login' />
          </div>
        </div>
      </form>
    </>
  );
}

export default SignIn;